
import React, { Suspense, lazy } from 'react';
import { HashRouter, Routes, Route, useLocation, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { supabase } from './lib/supabase';

// Lazy load everything to isolate module crashes
const WelcomeScreen = lazy(() => import('./views/WelcomeScreen'));
const QuestionnaireScreen = lazy(() => import('./views/QuestionnaireScreen'));
const RoutineSelectorScreen = lazy(() => import('./views/RoutineSelectorScreen'));
const DashboardScreen = lazy(() => import('./views/DashboardScreen'));
const MilestonesScreen = lazy(() => import('./views/MilestonesScreen'));
const CalendarScreen = lazy(() => import('./views/CalendarScreen'));
const AuraProgramsScreen = lazy(() => import('./views/AuraProgramsScreen'));
const WorkoutSessionScreen = lazy(() => import('./views/WorkoutSessionScreen'));
const WorkoutSummaryScreen = lazy(() => import('./views/WorkoutSummaryScreen'));
const CreateRoutineScreen = lazy(() => import('./views/CreateRoutineScreen'));
const WeeklyCheckinScreen = lazy(() => import('./views/WeeklyCheckinScreen'));
const SettingsScreen = lazy(() => import('./views/SettingsScreen'));
import SubscriptionScreen from './views/SubscriptionScreen';
const ManageSubscriptionScreen = lazy(() => import('./views/ManageSubscriptionScreen'));
import LandingPage from './views/LandingPage';
import LoginScreen from './views/LoginScreen';
const RegisterScreen = lazy(() => import('./views/RegisterScreen'));
const BasicWelcomeScreen = lazy(() => import('./views/BasicWelcomeScreen'));
const PremiumWelcomeScreen = lazy(() => import('./views/PremiumWelcomeScreen'));
const MaintenanceScreen = lazy(() => import('./views/MaintenanceScreen'));
const ForgotPasswordScreen = lazy(() => import('./views/ForgotPasswordScreen'));
const ResetPasswordScreen = lazy(() => import('./views/ResetPasswordScreen'));

// Admin Dashboard
import AdminRoute from './components/AdminRoute';
import { PWABanner } from './components/PWABanner';
const AdminLayout = lazy(() => import('./views/admin/AdminLayout'));
const AdminOverview = lazy(() => import('./views/admin/AdminOverview'));
const AdminUsersList = lazy(() => import('./views/admin/AdminUsersList'));
const AdminUserDetail = lazy(() => import('./views/admin/AdminUserDetail'));
const AdminSettings = lazy(() => import('./views/admin/AdminSettings'));
const AdminVideos = lazy(() => import('./views/admin/AdminVideos'));
const AdminPlanForge = lazy(() => import('./views/admin/AdminPlanForge'));

// Components (Directly imported as they are small and critical)
import NutritionDashboardScreen from './views/NutritionDashboardScreen';
import { InAppBrowserBypass } from './components/InAppBrowserBypass';
import AIAssistant from './components/AIAssistant';
import BottomNav from './components/BottomNav';
import { MarcTutorialOverlay } from './components/MarcTutorialOverlay';
import { PrivacyGuardModal } from './components/PrivacyGuardModal';
import { DietGlobalWorker } from './components/DietGlobalWorker';
import { SyncGlobalWorker } from './components/SyncGlobalWorker';
import { NutritionDashboardSkeleton } from './components/skeletons/NutritionDashboardSkeleton';
import ScreenLoader from './components/ScreenLoader';
import { Toaster } from 'sonner';
import { InstallPWAPrompt } from './components/InstallPWAPrompt';
import { PushNotificationPrompt } from './components/PushNotificationPrompt';
import { TestimonialModal } from './components/TestimonialModal';

// Robust Error Boundary
class GlobalErrorBoundary extends React.Component<{ children: React.ReactNode, name: string }, { hasError: boolean, error: any }> {
  constructor(props: { children: React.ReactNode, name: string }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: any) { return { hasError: true, error }; }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', background: '#0a0a0c', color: '#ef4444', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <span className="material-symbols-outlined" style={{ fontSize: '64px', marginBottom: '20px' }}>notification_important</span>
          <h2 style={{ fontSize: '24px', fontWeight: '900' }}>Error en: {this.props.name}</h2>
          <pre style={{ fontSize: '12px', marginTop: '20px', color: '#666' }}>{this.state.error?.toString()}</pre>
          <button onClick={() => window.location.reload()} style={{ marginTop: '20px', background: '#fff', color: '#000', padding: '10px 20px', borderRadius: '8px' }}>REINTENTAR</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const ProtectedRoute = () => {
  const { session, loading, onboardingCompleted, sessionInitialized, subscriptionStatus, isSyncing } = useAuth();
  const location = useLocation();

  if (loading || !sessionInitialized) {
    return <ScreenLoader />;
  }

  if (session) {
    const onboardingPaths = ['/questionnaire', '/welcome', '/basic-welcome'];
    if (!onboardingCompleted && !onboardingPaths.includes(location.pathname)) {
      return <Navigate to="/questionnaire" replace />;
    }

    // --- RESCUE TRAPPED USERS ---
    // If an existing user's cache was stale, they might have been forced to /questionnaire.
    // Once their true DB state loads (onboardingCompleted = true), we rescue them to the dashboard,
    // UNLESS they are actively running the new signup cinematic.
    if (onboardingCompleted && location.pathname === '/questionnaire' && sessionStorage.getItem('aura_is_processing_onboarding') !== 'true') {
      return <Navigate to="/dashboard" replace />;
    }

    // --- PREMIUM ONBOARDING REDIRECT ---
    const subStatus = subscriptionStatus || localStorage.getItem('aura_subscription_status');
    const isPro = subStatus === 'active' || subStatus === 'trialing' || subStatus === 'pro';

    // --- SUBSCRIBER-ONLY LOCK ---
    // If onboarding is completed and user is not Pro, force them to the subscription paywall
    if (onboardingCompleted && !isPro && location.pathname !== '/subscription') {
      console.log("[ProtectedRoute] Restricting access for free/inactive user. Redirecting to subscription.");
      return <Navigate to="/subscription" replace />;
    }

    let dbPremiumWelcomeSeen = false;
    try {
      const localDataRaw = localStorage.getItem('aura_user_data');
      if (localDataRaw) {
        let localData = JSON.parse(localDataRaw);
        if (typeof localData === 'string') localData = JSON.parse(localData);
        if (localData.premium_welcome_seen) {
          dbPremiumWelcomeSeen = true;
        }
      }
    } catch (e) {}
    
    console.log("[ProtectedRoute] Evaluation details:", {
      isPro,
      onboardingCompleted,
      premiumWelcomeSeen: (localStorage.getItem('aura_premium_welcome_seen') === 'true') || dbPremiumWelcomeSeen,
      isSyncing,
      dbPremiumWelcomeSeen,
      localUserData: localStorage.getItem('aura_user_data'),
      localWelcomeSeen: localStorage.getItem('aura_premium_welcome_seen'),
      pathname: location.pathname
    });

    const premiumWelcomeSeen = (localStorage.getItem('aura_premium_welcome_seen') === 'true') || dbPremiumWelcomeSeen;

    // If they are a new Premium user, redirect to Premium Welcome, UNLESS they are in the middle
    // of the Questionnaire cinematic (which will handle the navigation naturally).
    if (isPro && onboardingCompleted && !premiumWelcomeSeen && !isSyncing && location.pathname !== '/premium-welcome' && sessionStorage.getItem('aura_is_processing_onboarding') !== 'true') {
      console.log("[ProtectedRoute] Redirecting new premium user to welcome screen.");
      return <Navigate to="/premium-welcome" replace />;
    }
    // -----------------------------------

    return <Outlet />;
  }

  return <Navigate to="/login" replace />;
};

// Component to ensure navigation always starts at the top of the page smoothly
const ScrollToTop = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  React.useLayoutEffect(() => {
    // Synchronously force scroll to 0 before the browser repaints the new page DOM
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const container = document.getElementById('main-scroll-container');
    if (container) {
      container.scrollTop = 0;
    }
  }, [pathname]);
  
  return null;
};

const AppContent: React.FC = () => {
  const { session: authSession } = useAuth();
  const location = useLocation();
  const [isGeneratingTokenGlobal, setIsGeneratingTokenGlobal] = React.useState(false);
  
  React.useEffect(() => {
    // V1202: Removed undefined prefetchData hook to fix TS build error
  }, [authSession]);

  React.useEffect(() => {
    const handleTokenGenState = (e: Event) => {
      const customEvent = e as CustomEvent;
      setIsGeneratingTokenGlobal(!!customEvent.detail?.isGenerating);
    };
    window.addEventListener('aura_token_generation_state', handleTokenGenState);
    return () => window.removeEventListener('aura_token_generation_state', handleTokenGenState);
  }, []);

  const hideNavPaths = ['/', '/login', '/register', '/questionnaire', '/basic-welcome', '/workout-session', '/workout-summary', '/create-routine', '/weekly-checkin', '/settings', '/premium-welcome', '/forgot-password', '/reset-password', '/subscription', '/manage-subscription'];
  const showNav = !hideNavPaths.includes(location.pathname) && !location.pathname.startsWith('/admin') && !isGeneratingTokenGlobal;

  // Hide Chat on specific screens
  const hideChatPaths = ['/', '/login', '/register', '/questionnaire', '/welcome', '/basic-welcome', '/create-routine', '/settings', '/weekly-checkin', '/workout-session', '/forgot-password', '/reset-password', '/subscription', '/manage-subscription'];
  const showChat = !hideChatPaths.includes(location.pathname) && !location.pathname.startsWith('/admin') && !isGeneratingTokenGlobal;

  // PRIVACY GUARD CHECK
  const { user, session, userRole, loading, isSyncing, sessionInitialized, maintenanceMode } = useAuth();
  const [showPrivacyGuard, setShowPrivacyGuard] = React.useState(false);
  const { i18n } = useTranslation();

  // LANGUAGE SYNC: Update database profiles.stats.language when i18n language changes
  React.useEffect(() => {
    if (user && !loading && i18n.language) {
      const syncLanguageToDB = async () => {
        try {
          // V1150 FIX: 
          // Doing a direct JSONB replace (UPDATE { stats: newStats }) from the client 
          // introduces a lethal Read-Modify-Write race condition against backend processes 
          // like the Stripe Webhook or Diet Generators updating aura_diet_status asynchronously.
          // Therefore, we no longer forcefully rewrite the entire profiles.stats JSON here.
          // Language syncing will be incorporated lazily during safe local milestones instead.
          const localDataRaw = localStorage.getItem('aura_user_data');
          if (localDataRaw) {
             let localData = JSON.parse(localDataRaw);
             if (typeof localData === 'string') localData = JSON.parse(localData);
             if (localData.language !== i18n.language) {
                 localData.language = i18n.language;
                 localStorage.setItem('aura_user_data', JSON.stringify(localData));
                 window.dispatchEvent(new CustomEvent('aura_stats_updated'));
             }
          }
        } catch (err) {
          console.error("Failed to sync language locally:", err);
        }
      };
      // Minimal debounce to avoid rapid auth boot calls
      const timer = setTimeout(syncLanguageToDB, 2000);
      return () => clearTimeout(timer);
    }
  }, [user, loading, i18n.language]);

  React.useEffect(() => {
    // V601 FIX: Wait for both loading AND isSyncing to finish before evaluating PrivacyGuard
    if (user && !loading && !isSyncing) {
      const checkConsent = () => {
        try {
          const rawLocal = localStorage.getItem('aura_user_data');
          if (!rawLocal) return;
          
          let localData = JSON.parse(rawLocal);
          if (typeof localData === 'string') {
              try { localData = JSON.parse(localData); } catch (e) {}
          }
          
          // If consent is missing OR health_data_consent is explicitly false
          if (!localData.consent?.health_data_consent) {
            // Exclude onboarding screens where they *are* giving consent
            if (!['/register', '/login', '/questionnaire'].includes(location.pathname)) {
              setShowPrivacyGuard(true);
            }
          } else {
            setShowPrivacyGuard(false);
          }
        } catch (e) {
          setShowPrivacyGuard(false);
        }
      };
      checkConsent();
      // Listen for potential cross-tab updates
      window.addEventListener('storage', checkConsent);
      // Listen to our custom sync event for local updates
      window.addEventListener('aura_sync_complete', checkConsent);
      
      return () => {
        window.removeEventListener('storage', checkConsent);
        window.removeEventListener('aura_sync_complete', checkConsent);
      };
    } else if (!user) {
      setShowPrivacyGuard(false);
    }
  }, [user, loading, isSyncing, location.pathname]);

  // REFERRAL SYSTEM: Catch ?ref= globally and store in sessionStorage
  React.useEffect(() => {
    try {
      const refParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const ref = refParams.get('ref') || hashParams.get('ref');
      if (ref) {
        sessionStorage.setItem('aura_referred_by', ref);
      }
    } catch (e) { console.error("Referral parse error", e); }
  }, [location.search, location.hash]);

  // Handle Maintenance Mode globally
  if (maintenanceMode && userRole !== 'admin' && location.pathname !== '/maintenance') {
    return <Navigate to="/maintenance" replace />;
  }

  // GLOBAL SPLASH SCREEN OVERRIDE
  // This guarantees absolutely nothing (not even PWA banners or BottomNav)
  // renders while Auth is booting during the enforced minBootDelay.
  if (loading || !sessionInitialized) {
    return (
      <div className="min-h-screen bg-background-dark text-white" style={{ backgroundColor: '#050505', minHeight: '100vh', color: 'white' }}>
        <ScreenLoader />
      </div>
    );
  }

  return (
    <div className="h-[100dvh] w-full bg-background-dark/0 text-white flex flex-col relative overflow-hidden" style={{ width: '100%', color: 'white' }}>
      <ScrollToTop />
      <InAppBrowserBypass />
      
      {/* Scrollable page body */}
      <div className="flex-1 w-full overflow-y-auto overscroll-y-contain" id="main-scroll-container">
        <Suspense fallback={<ScreenLoader />}>
          {['/dashboard', '/calendar', '/routine-selector', '/aura-programs', '/create-routine', '/nutrition-dashboard', '/milestones'].includes(location.pathname) && (
            <>
              <PWABanner />
              <InstallPWAPrompt />
              <PushNotificationPrompt />
            </>
          )}

          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginScreen />} />
            <Route path="/register" element={<RegisterScreen />} />
            <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
            <Route path="/reset-password" element={<ResetPasswordScreen />} />
            <Route path="/welcome" element={<WelcomeScreen />} />
            <Route path="/questionnaire" element={<QuestionnaireScreen />} />
            <Route path="/basic-welcome" element={<BasicWelcomeScreen />} />
            <Route path="/premium-welcome" element={<PremiumWelcomeScreen />} />

            <Route element={<ProtectedRoute />}>
              <Route path="/dashboard" element={<DashboardScreen />} />
              <Route path="/calendar" element={<CalendarScreen />} />
              <Route path="/routine-selector" element={<RoutineSelectorScreen />} />
              <Route path="/workout-session" element={<WorkoutSessionScreen />} />
              <Route path="/workout-summary" element={<WorkoutSummaryScreen />} />
              <Route path="/create-routine" element={<CreateRoutineScreen />} />
              <Route path="/aura-programs" element={<AuraProgramsScreen />} />
              <Route path="/milestones" element={<MilestonesScreen />} />
              {/* Nutrition Dashboard now statically imported to prevent transition flash */}
              <Route path="/nutrition-dashboard" element={<NutritionDashboardScreen />} />
              <Route path="/weekly-checkin" element={<WeeklyCheckinScreen />} />
              <Route path="/settings" element={<SettingsScreen />} />
              <Route path="/subscription" element={<SubscriptionScreen />} />
              <Route path="/manage-subscription" element={<ManageSubscriptionScreen />} />
            </Route>

            <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
              <Route index element={<AdminOverview />} />
              <Route path="users" element={<AdminUsersList />} />
              <Route path="users/:id" element={<AdminUserDetail />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="videos" element={<AdminVideos />} />
              <Route path="forge" element={<AdminPlanForge />} />
            </Route>

            <Route path="/maintenance" element={<MaintenanceScreen />} />

            <Route path="*" element={<Navigate to={session ? "/dashboard" : "/"} replace />} />
          </Routes>
        </Suspense>

        <MarcTutorialOverlay />
        <DietGlobalWorker />
        <SyncGlobalWorker />
      </div>

      {showNav && (
        <GlobalErrorBoundary name="BottomNav">
          <BottomNav />
        </GlobalErrorBoundary>
      )}

      {showChat && (
        <GlobalErrorBoundary name="AI Marc Serrano">
          <AIAssistant />
        </GlobalErrorBoundary>
      )}

      <Toaster position="top-center" richColors theme="dark" />

      {showPrivacyGuard && <PrivacyGuardModal />}
      <TestimonialModal />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <GlobalErrorBoundary name="Root">
      <AuthProvider>
        <HashRouter>
          <AppContent />
        </HashRouter>
      </AuthProvider>
    </GlobalErrorBoundary>
  );
};

export default App;
